import { useMemo } from 'react';
import type { OpenAccountType } from './types';
import { useAssets, useAssetsIcons } from '@/shared/api';
import {
  useAccountsRests,
  useAccountsConfigurations,
  findConfigurationType,
  findConfigurationCode,
  hasEula,
  type AccountConfigurationCodeDto,
} from '@/entities/account';
import { usePendingAccountsStore } from './pendingAccountsStore';
import { useOpenAccountAvailabilityStore } from './useOpenAccountAvailabilityStore';

const CONFIG_TYPE_BY_ACCOUNT_TYPE: Record<OpenAccountType, string> = {
  spot: 'spot',
  margin: 'leverage',
  invest: 'invest',
  earn: 'earn',
};

const PENDING_TYPE_BY_ACCOUNT_TYPE: Record<
  OpenAccountType,
  'spot' | 'invest' | 'margin' | 'earn'
> = {
  spot: 'spot',
  margin: 'margin',
  invest: 'invest',
  earn: 'earn',
};

const useOpenAccountFormData = (
  accountType: OpenAccountType,
  selectedAccountCode?: string
) => {
  const { data: accountsRests } = useAccountsRests();
  const { pendingAccounts } = usePendingAccountsStore();
  const { accountsConfigurations, isAccountsConfigurationsLoading } =
    useAccountsConfigurations();
  const { codesAvailability } = useOpenAccountAvailabilityStore();
  const { data: assetsData } = useAssets({
    enabled: ['invest', 'margin', 'earn'].includes(accountType),
  });

  const { assetsIconsData } = useAssetsIcons({
    enabled: !!assetsData,
  });

  const configTypeCode = CONFIG_TYPE_BY_ACCOUNT_TYPE[accountType];

  const configurationType = useMemo(
    () => findConfigurationType(accountsConfigurations, configTypeCode),
    [accountsConfigurations, configTypeCode]
  );

  const availableCodes = useMemo(() => {
    if (!configurationType) {
      return [];
    }
    return configurationType.accountCodes.filter(code =>
      codesAvailability.some(
        availability =>
          availability.accountTypeCode === configTypeCode &&
          availability.accountCode === code.accountCode.toLowerCase() &&
          availability.canCreate
      )
    );
  }, [configurationType, codesAvailability, configTypeCode]);

  const activeCode: AccountConfigurationCodeDto | undefined = useMemo(() => {
    if (selectedAccountCode) {
      return (
        findConfigurationCode(configurationType, selectedAccountCode) ||
        availableCodes[0]
      );
    }
    return availableCodes[0];
  }, [configurationType, selectedAccountCode, availableCodes]);

  const eulaText = accountsConfigurations?.eula || '';

  const formFields = useMemo(() => {
    const fields = [];

    if (availableCodes.length > 1) {
      fields.push('tradingProductCode');
    }

    if (
      activeCode &&
      !activeCode.isMultiAsset &&
      activeCode.assets &&
      activeCode.assets.length > 0
    ) {
      fields.push('asset');
    }

    if (activeCode?.leverage && activeCode.leverage.length > 1) {
      fields.push('leverageValue');
    }

    if (hasEula(accountsConfigurations)) {
      fields.push('eula');
    }

    return fields;
  }, [availableCodes, activeCode, accountsConfigurations]);

  const assetCountsForActiveCode = useMemo(() => {
    const counts: Record<string, number> = {};

    if (!activeCode || accountType === 'spot') {
      return counts;
    }

    const restsKey = accountType === 'margin' ? 'leverage' : accountType;
    const codeLower = activeCode.accountCode.toLowerCase();

    accountsRests?.[restsKey]?.accounts
      .filter(
        account => account.tradingProductCode?.toLowerCase() === codeLower
      )
      .forEach(account => {
        const asset = account.balanceAsset?.code?.toLowerCase();
        if (asset) {
          counts[asset] = (counts[asset] || 0) + 1;
        }
      });

    pendingAccounts
      .filter(
        pending =>
          pending.accountType === PENDING_TYPE_BY_ACCOUNT_TYPE[accountType] &&
          (!pending.accountCode ||
            pending.accountCode.toLowerCase() === codeLower)
      )
      .forEach(pending => {
        const asset = pending.assetCode?.toLowerCase();
        if (asset) {
          counts[asset] = (counts[asset] || 0) + 1;
        }
      });

    return counts;
  }, [activeCode, accountType, accountsRests, pendingAccounts]);

  const typeSelectItems = useMemo(() => {
    return availableCodes.map(code => ({
      value: code.accountCode,
      typeCode: code.accountCodeName,
      typeDescription: code.accountCodeDescription || '',
    }));
  }, [availableCodes]);

  const assetSelectItems = useMemo(() => {
    if (!activeCode?.assets) {
      return [];
    }

    const byAssetLimit = activeCode.allowedAccountsQuantityByAsset;

    return activeCode.assets
      .filter(
        asset =>
          byAssetLimit === null ||
          byAssetLimit > (assetCountsForActiveCode[asset.toLowerCase()] || 0)
      )
      .map(item => ({
        value: item,
        code: item,
        name: assetsData?.value.find(asset => asset.code === item)?.name || '',
        icon: assetsIconsData?.value[item] || undefined,
      }));
  }, [activeCode, assetCountsForActiveCode, assetsData, assetsIconsData]);

  const leverageSelectItems = useMemo(() => {
    if (!activeCode?.leverage) {
      return [];
    }
    return activeCode.leverage.map(item => ({
      value: item.toString(),
      title: `1 : ${item}`,
    }));
  }, [activeCode]);

  return {
    isConfigurationLoading: isAccountsConfigurationsLoading,
    activeCode,
    availableCodes,
    eulaText,
    formFields,
    typeSelectItems,
    assetSelectItems,
    leverageSelectItems,
  };
};

export { useOpenAccountFormData };
